import { Component } from '@theme/component';

/**
 * Announcement banner. Desktop shows all items side-by-side (static).
 * Mobile auto-rotates through slides.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #interval = undefined;
  /** @type {MediaQueryList} */
  #mq = window.matchMedia('(max-width: 749px)');
  #mqHandler = () => this.#applyMode();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#applyMode();
    this.#mq.addEventListener('change', this.#mqHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#mq.removeEventListener('change', this.#mqHandler);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    clearInterval(this.#interval);
  }

  #applyMode() {
    const slides = this.refs.slides ?? [];
    if (slides.length <= 1) return;

    if (this.#mq.matches) {
      this.#enableSlide();
    } else {
      this.#enableStatic();
    }
  }

  #enableStatic() {
    this.removeAttribute('data-slide-mode');
    this.suspend();
    for (const slide of this.refs.slides ?? []) {
      slide.setAttribute('aria-hidden', 'false');
    }
  }

  #enableSlide() {
    this.setAttribute('data-slide-mode', '');
    this.current = 0;
    if (!this.#interval) this.play();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * @param {number} [interval]
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() {
    if (!this.autoplay || this.paused || !this.#mq.matches) return;
    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    if (Number.isNaN(value)) return undefined;
    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;
    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => {
    if (!this.#mq.matches) return;
    document.hidden ? this.pause() : this.resume();
  };
}

if (!customElements.get('ld-announcement-bar-component')) {
  customElements.define('ld-announcement-bar-component', AnnouncementBar);
}
